import React, { useCallback, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { useOnClickOutside } from '@app/helpers';

import { variants } from './variants';

import {
    Chevron,
    DropDownContent,
    DropdownContent,
    DropDownDescription,
    DropDownItem,
    DropDownText,
    MainText,
    Outer,
    Wrapper,
} from './styles';

export interface DropdownOption {
    value: string | number;
    label: string;
    disabled?: boolean;
    description?: string;
}

interface DropdownProps {
    selected: DropdownOption;
    options: DropdownOption[];
    onClick(value: DropdownOption): void;
    variant?: 'default' | 'simplified';
    className?: string;
}

const Dropdown = ({ selected, options, onClick, variant, className }: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const simplified = variant === 'simplified';

    useOnClickOutside(dropdownRef, () => {
        setIsOpen(false);
    });

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleSelect = useCallback(
        (option: DropdownOption) => {
            setIsOpen(false);
            onClick(option);
        },
        [onClick],
    );

    const handleKeyboardPress = useCallback(
        (e, option: DropdownOption) => {
            if (e.key === 'Enter' || e.key === 'Space') {
                e.preventDefault();
                handleSelect(option);
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                setIsOpen(false);
            }
        },
        [handleSelect],
    );

    return (
        <Outer isOpen={isOpen} ref={dropdownRef} className={`${className}`} $simplified={simplified}>
            <Wrapper isOpen={isOpen} onClick={handleToggle} $simplified={simplified}>
                <MainText $simplified={simplified}>{selected.label}</MainText>
                <Chevron $isOpen={isOpen} />
            </Wrapper>
            <AnimatePresence>
                {isOpen && (
                    <DropdownContent
                        $simplified={simplified}
                        animate="visible"
                        exit="hidden"
                        initial="hidden"
                        variants={simplified ? variants : undefined}
                    >
                        {options.map((option: DropdownOption) => {
                            const isActive = selected.value === option.value;
                            return (
                                <DropDownItem
                                    tabIndex={1}
                                    key={option.value}
                                    isDisabled={option.disabled}
                                    isActive={isActive}
                                    $simplified={simplified}
                                    onClick={() => {
                                        if (!option.disabled) handleSelect(option);
                                    }}
                                    onKeyDown={(e) => {
                                        if (!option.disabled) handleKeyboardPress(e, option);
                                    }}
                                >
                                    <DropDownContent>
                                        <DropDownText>{option.label}</DropDownText>
                                        {option.description && (
                                            <DropDownDescription title={option.description}>
                                                {option.description}
                                            </DropDownDescription>
                                        )}
                                    </DropDownContent>
                                    {/* {isActive && (
                                        <DropDownCheck>
                                            <Check></Check>
                                        </DropDownCheck>
                                    )} */}
                                </DropDownItem>
                            );
                        })}
                    </DropdownContent>
                )}
            </AnimatePresence>
        </Outer>
    );
};

export default Dropdown;
